import { useCallback, useMemo, useState } from "react"
import type { StoragePageFilters, StoragePageResponse } from "@/schema/storage-file"
import { useQueries, useQueryClient, type QueryKey, type UseQueryResult } from "@tanstack/react-query"

import { apiContextFetch, type ApiContext } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import type { StorageRecordType } from "./api"
import { storageKeys } from "./index"

export function browseStoragePage(
  context: ApiContext,
  route: string,
  folderId: string | undefined,
  offset: number,
  filters: StoragePageFilters,
  signal: AbortSignal
) {
  const params = new URLSearchParams({ paged: "true", offset: String(offset) })
  if (folderId) params.set("folder_id", folderId)
  if (filters.foldersOnly) params.set("folders_only", "true")
  if (filters.nameContains) params.set("name_contains", filters.nameContains)
  return apiContextFetch<StoragePageResponse>(context, `${route}?${params}`, { signal })
}

export interface StoragePageSource {
  id: string
  key: QueryKey
  route: string
  folderId?: string
  parentId?: string
  allowUnlisted?: boolean
  filters?: StoragePageFilters
}

export function useStoragePages(sources: StoragePageSource[], bookId?: string, contextKey?: string) {
  const getContext = useApiContext(bookId)
  const client = useQueryClient()
  const [requested, setRequested] = useState<Record<string, number[]>>({})
  const [previousContext, setPreviousContext] = useState(contextKey)
  if (previousContext !== contextKey) {
    setPreviousContext(contextKey)
    setRequested({})
  }
  const offsetsFor = (source: StoragePageSource) =>
    (previousContext === contextKey ? requested[source.folderId ?? source.id] : undefined) ?? [0]
  const sourceById = new Map(sources.map((source) => [source.id, source]))
  function isReachable(source: StoragePageSource): boolean {
    if (!source.parentId) return true
    const parent = sourceById.get(source.parentId)
    if (!parent || !isReachable(parent)) return false
    return (
      !!source.allowUnlisted ||
      offsetsFor(parent).some((offset) =>
        client
          .getQueryData<StoragePageResponse>([...parent.key, offset])
          ?.files.some((file) => file.id === source.folderId)
      )
    )
  }
  const reachableIds = JSON.stringify(sources.filter(isReachable).map((source) => source.id))
  const activeSources = useMemo(() => {
    const ids: string[] = JSON.parse(reachableIds)
    return sources.filter((source) => ids.includes(source.id))
  }, [sources, reachableIds])
  const requests = useMemo(
    () =>
      activeSources.flatMap((source) =>
        ((previousContext === contextKey ? requested[source.folderId ?? source.id] : undefined) ?? [0]).map(
          (offset) => ({ source, offset })
        )
      ),
    [activeSources, requested, previousContext, contextKey]
  )
  // Refresh the combined result when requests change, before the query observers
  // catch up, while preserving structural sharing on unrelated renders.
  const combine = useCallback(
    (results: UseQueryResult<StoragePageResponse>[]) =>
      requests.map((_, index) => {
        const { data, error, errorUpdatedAt, isPending, isFetching, refetch } = results[index]
        return { data, error, errorUpdatedAt, isPending, isFetching, refetch }
      }),
    [requests]
  )
  const queries = useQueries({
    combine,
    queries: requests.map(({ source, offset }) => ({
      queryKey: [...source.key, offset],
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        browseStoragePage(await getContext(), source.route, source.folderId, offset, source.filters ?? {}, signal),
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
    })),
  })
  const folders = useMemo(
    () =>
      new Map(
        activeSources.map((source) => {
          const pages = queries.filter((_, index) => requests[index].source.id === source.id)
          const last = pages.at(-1)!
          const nextOffset = last.data?.next_offset
          const files = new Map<string, StoragePageResponse["files"][number]>()
          for (const page of pages)
            for (const file of page.data?.files ?? []) if (!files.has(file.id)) files.set(file.id, file)
          return [
            source.id,
            {
              files: Array.from(files.values()),
              parent_folder: pages[0]?.data?.parent_folder,
              isLoading: !pages[0]?.data && pages[0]?.isPending,
              error: pages.find((page) => page.error)?.error ?? null,
              errorUpdatedAt: Math.max(...pages.map((page) => page.errorUpdatedAt)),
              hasNextPage: nextOffset != null || (pages.length > 1 && last.isPending),
              loadMore: () => {
                if (nextOffset == null || last.isFetching || last.error) return
                const key = source.folderId ?? source.id
                setRequested((prev) => {
                  const offsets = prev[key] ?? [0]
                  return offsets.includes(nextOffset) ? prev : { ...prev, [key]: [...offsets, nextOffset] }
                })
              },
              retry: () => {
                for (const page of pages) if (page.error) void page.refetch()
              },
            },
          ] as const
        })
      ),
    [activeSources, queries, requests]
  )

  function restart(folderId: string) {
    const affected = sources.filter((source) => source.folderId === folderId)
    setRequested((prev) => ({ ...prev, [folderId]: [0] }))
    return Promise.all(affected.map((source) => client.refetchQueries({ queryKey: [...source.key, 0], exact: true })))
  }
  return { folders, restart }
}

export function useStoragePickerPages({
  integrationId,
  recordType,
  recordId,
  folderId,
  rootFolderId,
  bookId,
  enabled = true,
  manageFolders = false,
  foldersOnly,
  nameContains,
}: StoragePageFilters & {
  integrationId?: string
  recordType?: StorageRecordType
  recordId?: string
  folderId?: string
  rootFolderId?: string
  bookId?: string
  enabled?: boolean
  manageFolders?: boolean
}) {
  const urlBookId = useBookId()
  const resolvedBookId = bookId ?? urlBookId
  const route =
    recordId && recordType
      ? `${recordType}/${recordId}/storage/browse`
      : `storage_integrations/${integrationId}/${manageFolders ? "browse" : "picker_items"}`
  const prefix =
    recordId && recordType
      ? storageKeys.browse(recordType, recordId, folderId)
      : ["storage_integrations", manageFolders ? "browse" : "picker_items", integrationId, folderId, resolvedBookId]
  const key = [...prefix, "paged", integrationId, resolvedBookId, rootFolderId, { foldersOnly, nameContains }]
  const { folders } = useStoragePages(
    enabled && (recordId || integrationId)
      ? [{ id: "picker", key, route, folderId, filters: { foldersOnly, nameContains } }]
      : [],
    bookId,
    JSON.stringify([enabled, key])
  )
  return (
    folders.get("picker") ?? {
      files: [],
      parent_folder: undefined,
      isLoading: false,
      error: null,
      errorUpdatedAt: 0,
      hasNextPage: false,
      loadMore() {},
      retry() {},
    }
  )
}
